// Application logger utility
// Use this to log errors and events throughout the application

import { supabase } from '@/integrations/supabase/client';

export type LogLevel = 'error' | 'warning' | 'info' | 'debug';

interface LogOptions {
  level?: LogLevel;
  context?: string;
  stack?: string;
  metadata?: any;
  userId?: string;
  userEmail?: string;
}

/**
 * Log an error or event to the database
 */
export const log = async (
  message: string,
  options: LogOptions = {}
): Promise<void> => {
  try {
    const {
      level = 'info',
      context,
      stack,
      metadata,
      userId,
      userEmail
    } = options;

    // Get current user if not provided
    const { data: { user } } = await supabase.auth.getUser();
    const finalUserId = userId || user?.id;
    const finalUserEmail = userEmail || user?.email;

    // Get current URL and user agent
    const url = typeof window !== 'undefined' ? window.location.href : undefined;
    const userAgent = typeof window !== 'undefined' ? navigator.userAgent : undefined;

    // Insert log into database - use any type since table may not exist
    await (supabase as any).from('application_logs').insert({
      level,
      message,
      context,
      stack,
      user_id: finalUserId,
      user_email: finalUserEmail,
      url,
      user_agent: userAgent,
      metadata: metadata || {}
    });
  } catch (error) {
    // Fallback to console if database logging fails
    console.error('Failed to log to database:', error);
    console.error('Original log:', { message, ...options });
  }
};

/**
 * Convenience methods for different log levels
 */
export const logger = {
  error: (message: string, options?: Omit<LogOptions, 'level'>) =>
    log(message, { ...options, level: 'error' }),
  
  warning: (message: string, options?: Omit<LogOptions, 'level'>) =>
    log(message, { ...options, level: 'warning' }),
  
  info: (message: string, options?: Omit<LogOptions, 'level'>) =>
    log(message, { ...options, level: 'info' }),
  
  debug: (message: string, options?: Omit<LogOptions, 'level'>) =>
    log(message, { ...options, level: 'debug' }),
};

/**
 * Log an error with stack trace
 */
export const logError = async (
  error: Error | unknown,
  context?: string,
  metadata?: any
): Promise<void> => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  await logger.error(errorMessage, {
    context: context || 'Application Error',
    stack,
    metadata: {
      ...metadata,
      errorType: error instanceof Error ? error.constructor.name : typeof error
    }
  });
};
